import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { slugify } from '../../utils/helpers';
import { ChevronRight, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BlogDetail() {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/public/blog`).then(res => {
      setRecentBlogs(res.data.slice(0, 5)); // Get latest 5
      const found = res.data.find(b => b.id === id || slugify(b.title) === id);
      setBlog(found);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center">Yükleniyor...</div>;
  
  if (!blog) return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center p-6 text-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Yazı bulunamadı.</h2>
          <a href="/#blog" className="text-primary hover:underline font-medium">Ana Sayfaya Dön</a>
        </div>
      </main>
      <Footer />
    </div>
  );

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API}${url}`;
  };

  const formatDate = (isoString) => {
    try {
      return format(parseISO(isoString), 'dd.MM.yyyy');
    } catch {
      return '';
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen flex flex-col">
      <Navbar />
      
      {/* HEADER BREADCRUMB */}
      <div className="bg-slate-900 py-12 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-white mb-4 line-clamp-2">{blog.title}</h1>
          <div className="flex items-center text-sm text-slate-400 gap-4">
            <span className="flex items-center gap-1.5"><CalendarIcon className="w-4 h-4" /> {formatDate(blog.created_at)}</span>
            <span>•</span>
            <div className="flex items-center gap-2">
              <a href="/" className="hover:text-primary transition-colors">Ana Sayfa</a>
              <ChevronRight className="w-3 h-3" />
              <a href="/#blog" className="hover:text-primary transition-colors">Blog</a>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          
          {/* LEFT CONTENT AREA */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100">
              {blog.image && (
                <div className="aspect-video mb-8 rounded-2xl overflow-hidden shadow-sm">
                  <img src={getImageUrl(blog.image)} alt={blog.title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="prose prose-slate prose-lg max-w-none prose-headings:font-heading prose-headings:text-slate-900 prose-p:text-slate-600 prose-a:text-primary prose-strong:text-slate-900">
                {blog.content?.split('\n').map((para, i) => (
                  para.trim() ? <p key={i} className="mb-6 leading-relaxed">{para}</p> : null
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT SIDEBAR */}
          <div className="lg:col-span-1 space-y-8">
            
            {/* Sidebar Widget 1: Recent Blogs */}
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
              <h3 className="text-xl font-bold font-heading text-slate-900 mb-6 pb-4 border-b border-slate-100 flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" /> Son Yazılar
              </h3>
              <div className="space-y-6">
                {recentBlogs.filter(b => slugify(b.title) !== slugify(blog.title)).map(b => (
                  <a key={slugify(b.title)} href={`/blog/${slugify(b.title)}`} className="group flex gap-4 items-start">
                    <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-slate-100">
                      {b.image && <img src={getImageUrl(b.image)} alt={b.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />}
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800 text-sm mb-1 group-hover:text-primary transition-colors line-clamp-2">{b.title}</h4>
                      <p className="text-xs text-slate-500">{formatDate(b.created_at)}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>

          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
